import React, { useEffect, useState } from "react";
import { Check, Eye, EyeOff, Lock, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ToastSuccess from "../../../(common)/Toast/ToastSuccess";
import ToastWarning from "../../../(common)/Toast/ToastWarning";
import { TEXTS } from "../../constants/texts";
import { passwordCriteria } from "../../constants/validationRules";
import { buttonStyles, StyledButton } from "../../../../Shared/button/button";
import { useFormik, FormikHelpers } from "formik";
import * as Yup from "yup";
import WithAuthLayout from "../withAuthLayout";
import { Input } from "../../(components)/ui/input";
import {
  isPasswordResetRedirectInvalid,
  parsePasswordResetRedirect,
  resetUserPassword,
} from "../../../../utils/passwordReset";
import { testIdProps } from "../../../../utils/testIdProps";

const successMessage = [
  TEXTS.RESET_PASSWORD_SUCCESSFULLY,
  TEXTS.RESET_PASSWORD_MESSAGE,
];

interface FormValues {
  password: string;
  confirmPassword: string;
}

const resetPasswordSchema = Yup.object({
  password: Yup.string()
    .min(8)
    .max(15)
    .matches(/[A-Z]/, "Must include an uppercase letter")
    .matches(/[a-z]/, "Must include a lowercase letter")
    .matches(/\d/, "Must include a digit")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "Must include a special character")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm your password"),
});

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordResetSuccessfully, setPasswordResetSuccessfully] =
    useState(false);
  const [resetToken, setResetToken] = useState("");
  const [linkReady, setLinkReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const redirectParams = parsePasswordResetRedirect(window.location.search);

    if (isPasswordResetRedirectInvalid(redirectParams)) {
      navigate("/password-reset-link-expiry", { replace: true });
      return;
    }

    if (redirectParams.resetToken) {
      setResetToken(redirectParams.resetToken);
      window.history.replaceState({}, "", window.location.pathname);
    }

    setLinkReady(true);
  }, [navigate]);

  const formik = useFormik<FormValues>({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: resetPasswordSchema,
    onSubmit: async (
      values: FormValues,
      { setSubmitting }: FormikHelpers<FormValues>
    ) => {
      await handleSubmit(values);
      setSubmitting(false);
    },
  });

  const handleSubmit = async (values: FormValues) => {
    if (!resetToken) {
      setError(TEXTS.PASSWORD_RESET_INVALID_LINK);
      return;
    }

    if (values.password === "") return setError("Password is required");
    if (values.confirmPassword === "")
      return setError("Confirm password is required");

    const allCriteriaPassed = passwordCriteria.every(({ test }) =>
      test(values.password)
    );
    if (!allCriteriaPassed)
      return setError("Password does not meet all criteria as mentioned.");
    if (values.password !== values.confirmPassword)
      return setError("Passwords do not match.");

    setError("");

    try {
      setLoading(true);
      await resetUserPassword(resetToken, values.password);

      ToastSuccess("", successMessage);
      setPasswordResetSuccessfully(true);
    } catch (err) {
      ToastWarning("", (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (!linkReady) {
    return null;
  }

  if (!resetToken && !passwordResetSuccessfully) {
    return (
      <main className="d-flex align-items-center justify-content-center">
        <div
          className="container d-flex flex-column shadow bg-white border rounded-4 px-4 py-4 text-center"
          style={{ maxWidth: 400 }}
        >
          <h2 className="fs-5 fw-bold mb-3">Invalid reset link</h2>
          <p className="text-muted small mb-4">
            {TEXTS.PASSWORD_RESET_INVALID_LINK}
          </p>
          <StyledButton
            className="w-100 py-2"
            type="button"
            onClick={() => navigate("/forgot-password")}
            id="request-new-reset-link"
            style={buttonStyles.primary.normal}
            hoverStyle={buttonStyles.primary.hover}
          >
            Request a new link
          </StyledButton>
        </div>
      </main>
    );
  }

  return (
    <main className="d-flex align-items-center gap-5 justify-content-between">
      {passwordResetSuccessfully ? (
        <div
          className="d-flex flex-column justify-content-center align-items-center px-3"
          style={{ height: "calc(100vh - 74px)" }}
        >
          <div className="bg-white border rounded-4 shadow p-4 text-center">
            <h2 className="fs-4 fw-bold mb-2">Password Reset Successful!</h2>
            <p
              style={{ color: "#8E8E8E", fontSize: "14px" }}
              className="mt-4 mb-4"
            >
              Password reset successfully, please proceed to login
              <br /> with the updated password.
            </p>
            <StyledButton
              className="w-100 py-2"
              type="button"
              onClick={() => navigate("/login")}
              id="reset-password-back-to-login"
              style={buttonStyles.primary.normal}
              hoverStyle={buttonStyles.primary.hover}
            >
              Go to login
            </StyledButton>
            <div className="text-center small text-muted mt-3 d-flex align-items-center justify-content-center gap-2">
              <Lock size={14} />
              Your data is secure with us
            </div>
          </div>
        </div>
      ) : (
        <form
          onSubmit={formik.handleSubmit}
          className="container d-flex flex-column shadow bg-white border rounded-4 px-4 py-4"
          style={{
            maxWidth: 400,
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          }}
        >
          <h2 className="fs-4 fw-bold mb-3">Reset Password</h2>

          {error && (
            <div className="alert alert-danger py-2 small" role="alert">
              {error}
            </div>
          )}

          <section className="d-flex flex-column w-100 gap-2 mb-3">
            <div className="">
              <Input
                id="reset-password"
                type={showPassword ? "text" : "password"}
                name="password"
                className={`form-control ${
                  formik.touched.password && formik.errors.password
                    ? "is-Invalid"
                    : ""
                }`}
                placeholder="Password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="new-password"
                suffixIcon={
                  <div
                    {...testIdProps("reset-password-toggle")}
                    className="position-absolute"
                    style={{
                      right: "2px",
                      cursor: "pointer",
                      height: "38px",
                      width: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <Eye size={18} color="#C4C4C4" />
                    ) : (
                      <EyeOff size={18} color="#C4C4C4" />
                    )}
                  </div>
                }
              />
              {formik.touched.password && formik.errors.password && (
                <div className="text-xs text-danger d-block">
                  {formik.errors.password}
                </div>
              )}
            </div>

            <div className="">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                className={`form-control ${
                  formik.touched.confirmPassword && formik.errors.confirmPassword
                    ? "is-Invalid"
                    : ""
                }`}
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formik.values.confirmPassword}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                autoComplete="new-password"
                suffixIcon={
                  <div
                    {...testIdProps("confirm-password-toggle")}
                    className="position-absolute"
                    style={{ right: "10px", cursor: "pointer" }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <Eye size={18} color="#C4C4C4" />
                    ) : (
                      <EyeOff size={18} color="#C4C4C4" />
                    )}
                  </div>
                }
              />

              {formik.touched.confirmPassword &&
                formik.errors.confirmPassword && (
                  <div className="text-xs text-danger d-block">
                    {formik.errors.confirmPassword}
                  </div>
                )}
            </div>
          </section>

          <section className="mb-4">
            <span
              className="fw-semibold text-muted d-block mb-2"
              style={{ fontSize: "14px" }}
            >
              Password Conditions:
            </span>
            <ul
              className="list-unstyled small mb-0"
              style={{ fontSize: "12px" }}
            >
              {passwordCriteria.map(({ id, text, test }) => {
                const passed = test(formik.values.password);
                const visible = formik.values.password.length > 0;

                return (
                  <li
                    key={id}
                    className={`d-flex align-items-center gap-2 ${visible ? (passed ? "text-success" : "text-danger") : "text-muted"}`}
                  >
                    {visible ? (
                      passed ? (
                        <Check className="me-1" size={16} />
                      ) : (
                        <X className="me-1" size={16} />
                      )
                    ) : (
                      <span
                        className="d-inline-block rounded-circle bg-secondary"
                        style={{ width: 6, height: 6 }}
                      ></span>
                    )}
                    {text}
                  </li>
                );
              })}
            </ul>
          </section>

          <StyledButton
            id="reset-password-submit"
            type="submit"
            isLoading={formik.isSubmitting || loading}
            style={
              !formik.isValid || !formik.dirty || formik.isSubmitting || loading
                ? buttonStyles.disabled.normal
                : buttonStyles.primary.normal
            }
            hoverStyle={
              !formik.isValid || !formik.dirty || formik.isSubmitting || loading
                ? buttonStyles.disabled.hover
                : buttonStyles.primary.hover
            }
          >
            Submit
          </StyledButton>

          <div className="text-center small text-muted mt-3 d-flex align-items-center justify-content-center gap-2">
            <Lock size={14} />
            Your data is secure with us
          </div>
        </form>
      )}
    </main>
  );
};

export default WithAuthLayout(ResetPassword);
